import { inject, Injectable } from '@angular/core';
import { ApiGateway } from '@sb/shared/api';
import { Observable } from 'rxjs';

/**
 * Single HTTP client for the server-owned AI onboarding endpoints. The backend
 * owns the prompt, output schema, model and token budget for each operation, so
 * the frontend only sends inputs and reads the typed JSON result — there is no
 * LLM SDK, prompt or schema in the browser.
 *
 * It centralises the interceptor policy shared by every AI call: results are
 * always surfaced inline by the caller, so the global success/error meta toast
 * is suppressed (`ignoreMetaMessage`). Each operation's endpoint path and its
 * request/result models are owned by the domain service that calls it — each
 * introduced in its own stacked PR — so this stays a single typed entry point
 * rather than one method per operation (foundation cannot import models that
 * only exist in the later PRs).
 *
 * Resolved through `ApiGateway` so these calls share the `/api/` prefix,
 * versioning and interceptors of every other request; the gateway also unwraps
 * the `{ data }` envelope, so `TResult` is the inner payload. The anonymous
 * signup call carries its reCAPTCHA token in the body (the gateway does not
 * forward custom headers), which the backend reads there.
 */
@Injectable({ providedIn: 'root' })
export class AiOnboardingApiService {
  #api = inject(ApiGateway);

  /**
   * emit the typed, envelope-unwrapped result. The global meta toast is
   * suppressed because callers render AI outcomes inline. Errors propagate for
   * the caller to map to its own error state.
   */
  post<TResult>(endpoint: string, inputs: object): Observable<TResult> {
    return this.#api.post<TResult>(endpoint, inputs, { headers: { ignoreMetaMessage: true } });
  }
}
